import React, { useEffect } from 'react';
import clsx from 'clsx';
import type { ErrorState } from '../types';

interface ErrorBannerProps {
  error: ErrorState;
  onDismiss: () => void;
}

export function ErrorBanner({ error, onDismiss }: ErrorBannerProps) {
  useEffect(() => {
    if (error.temporary) {
      const timer = setTimeout(onDismiss, 5000);
      return () => clearTimeout(timer);
    }
  }, [error.temporary, onDismiss]);

  return (
    <div
      className={clsx(
        "fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:max-w-md shadow-lg p-4 animate-slide-up bg-base-800 rounded-lg border",
        // Error severity styling
        error.type === 'error' && "border-secondary-500/50",
        error.type === 'unknown_error' && "border-secondary-500/50",
        // Auth errors
        (error.type === 'auth_failed' || error.type === 'auth_timeout') && "border-secondary-500/50",
        error.type === 'forbidden' && "border-secondary-500/50",
        // Rate limiting and temporary issues
        error.type === 'rate_limited' && "border-accent-500/50",
        error.type === 'reconnecting' && "border-accent-500/50",
        // Server and network errors
        (error.type === 'server_error' || error.type === 'bad_gateway' || error.type === 'service_unavailable') && "border-accent-600/50",
        (error.type === 'timeout' || error.type === 'gateway_timeout') && "border-accent-500/50",
        error.type === 'network_error' && "border-accent-500/50",
        // Content policy
        error.type === 'moderation_blocked' && "border-secondary-500/50",
        // Request errors
        error.type === 'bad_request' && "border-secondary-400/50",
        // LLM specific
        error.type === 'llm_unavailable' && "border-accent-600/50",
        // Default
        !error.type && "border-base-700"
      )}
      role="alert"
      aria-live="assertive"
    >
      <div className="flex items-start gap-3">
        <span className="text-2xl flex-shrink-0" aria-hidden="true">
          {error.icon || '⚠️'}
        </span>
        
        <div className="flex-1">
          {error.title && (
            <h3 className="font-semibold mb-1 text-base-100">{error.title}</h3>
          )}
          <p className="text-sm text-base-200">{error.message}</p>
          
          {error.showPolicyLink && (
            <a 
              href="/content-policy" 
              className="text-sm underline mt-2 inline-block text-primary-400 hover:text-primary-300 transition-colors"
              target="_blank"
              rel="noopener noreferrer"
            >
              View content policy
            </a>
          )}
          
          {error.showLogin && (
            <a 
              href="/login" 
              className="text-sm underline mt-2 inline-block text-primary-400 hover:text-primary-300 transition-colors"
            >
              Log in again
            </a>
          )}
          
          {error.retryAfter && (
            <p className="text-xs mt-2 text-base-400">
              You can try again in {error.retryAfter} seconds
            </p>
          )}
          
          {error.status && (
            <p className="text-xs mt-1 text-base-500">
              Error code: {error.status}
            </p>
          )}
        </div>
        
        <button
          onClick={onDismiss}
          className="transition text-base-400 hover:text-base-100"
          aria-label="Dismiss error"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}